<template>
  <div>
    <Datepicker
      v-model="inputValue"
      range
      placeholder="Search"
      class="mb-0 work-week-filter"
      name="start_date"
      label="Work Week"
      no-label
      :editable="false"
      popup-class="work-week-filter"
      @clear-date="resetWeek"
      @pick="handleDateSelect"
    >
      <template #header>
        <div class="d-flex align-items-center justify-content-center">
          <sera-toggle :selected="selectedWeeks" @change="handleWeeksSelect">
            <sera-toggle-item
              v-for="week in workWeekOptions"
              :key="week"
              :label="week"
            >
            </sera-toggle-item>
          </sera-toggle>
          <sera-button
            link
            icon="calendar"
            iconRight
            label="Current Week"
            class="ml-3"
            @click="resetWeek"
          ></sera-button>
        </div>
      </template>
    </Datepicker>
  </div>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, onMounted, ref } from "vue";

import Datepicker from "@/components/Datepicker.vue";
import CONSTANTS from "@/Constants";
const { DATE_API_FORMAT } = CONSTANTS;

const props = defineProps<{ modelValue: Array<string> | string | null }>();
const emit = defineEmits(["update:modelValue"]);

const workWeekOptions = ref(["1 Week", "2 Weeks", "3 Weeks", "4 Weeks"]);
const selectedWeeks = ref("1 Week");

onMounted(() => {
  // Check initial inputValue to correctly set selectedWeeks to match the range
  if (inputValue.value) {
    setSelectedWeeks(inputValue.value);
  }
});

const inputValue = computed<any>({
  get() {
    return props.modelValue;
  },
  set(val: Array<string>) {
    if (!val) {
      // Dates have been cleared, reset to current week
      return resetWeek();
    }
    setSelectedWeeks(val);
    const value: string = !val[0] && !val[1] ? null : getDateRange(val || []);
    emit("update:modelValue", value);
  }
});

const getDateRange = (range) =>
  range.map((date) => moment(date).format(DATE_API_FORMAT)).join("-");

const resetWeek = () => {
  // Reset to 1 week
  selectedWeeks.value = "1 Week";
  // Reset to current week
  inputValue.value = [
    moment().startOf("week").format("L"),
    moment().endOf("week").format("L")
  ];
};

const setSelectedWeeks = (dates: Array<string>) => {
  if (dates) {
    const diff = Math.floor(
      moment(dates[1]).diff(moment(dates[0]), "days") / 6
    );
    selectedWeeks.value = `${diff} ${diff > 1 ? "Weeks" : "Week"}`;
  }
};

const handleWeeksSelect = ({ detail }) => {
  selectedWeeks.value = detail;
  setRangeFromWeeks();
};

const setRangeFromWeeks = () => {
  if (!inputValue.value) return;
  const weeks = parseInt(selectedWeeks.value.substring(0, 1));
  inputValue.value = [
    inputValue.value[0],
    moment(inputValue.value[0]).add(weeks, "weeks").subtract(1, "day").toDate()
  ];
};

const handleDateSelect = (date) => {
  // Auto-selects a week
  inputValue.value = [date, moment(date).add(6, "days").toDate()];
};

defineExpose({ inputValue });
</script>

<style>
.work-week-filter .mx-datepicker-content {
  flex-direction: column;
}
</style>
